<template>
    <section class="layout layout-scale overflow-hidden">
        <div class="relative" style="z-index: 1">
            <QuizHeader
                :title="stripHtmlTag(title)"
                :current-quiz-index="currentQuizIndex"
                :question-count="questionCount"
            />
            <BgMusicMuteBtn :is-muted="isMuted" class="mr-6 md:mr-8"></BgMusicMuteBtn>
            <ClosePlayingButton :url="quiz.url" :embed="embedToken" />
        </div>

        <div
            @click="eventNext"
            :class="{ 'animated fadeIn': isAlreadyShow, 'opacity-0': !isAlreadyShow }"
            class="content mx-0 flex items-center"
            id="content-result"
        >
            <div class="mx-auto content-container">
                <div class="flex items-center justify-center flex-col mb-12 overflow-hidden pt-5" style="height: 550px">
                    <div ref="header" id="question-header" class="text-center"></div>
                    <div ref="headerMobile" id="question-header-mobile" class="text-center"></div>
                    <div class="text-center status-container">
                        <ResultIconAnimation :is-correct="playerResult" stroke="20" stroke-mark="23" />
                        <h2
                            class="text-center theme-ui-text-color theme-question-shadow font-size-30 font-bold uppercase mt-6"
                            :class="{ 'mb-0': !playerResult && showCorrectAnswerWhenWrong }"
                        >
                            {{ playerResult ? $t('playalone_result_correct') : $t('playalone_result_wrong') }}!
                        </h2>
                        <div
                            v-if="!playerResult && showCorrectAnswerWhenWrong"
                            @click.prevent="checkShowCorrectAnswerWhenWrong"
                            ref="showCorrectBtn"
                            class="show-correct-btn uppercase font-size-15 font-opensans mb-3 theme-ui-text-color underline cursor-pointer p-3"
                        >
                            {{ $t('playalone_result_show_correct') }}
                        </div>
                    </div>
                    <div
                        v-if="isFrontPageQuiz"
                        class="pt-3 sm:pt-12 pb-5 text-center fadeIn animated message-container"
                    >
                        <p
                            v-for="(msg, index) in message"
                            :key="index"
                            class="theme-ui-text-color theme-question-shadow font-size-18"
                        >
                            {{ msg }}
                        </p>
                    </div>
                    <div v-else class="empty-box-message theme-ui-text-color theme-question-shadow"></div>
                </div>
            </div>
        </div>
        <div class="footer" v-show="showFooter" :style="{ paddingBottom: type == 'poll' ? '15px' : false }">
            <div class="pb-4">
                <div class="flex flex-col items-center justify-center">
                    <div
                        v-if="!isEndQuiz"
                        @click="handleClickNext"
                        :disabled="isNextEnable"
                        class="theme-button btn btn-large uppercase font-opensans font-size-22"
                        id="next-btn"
                    >
                        {{ $t('playalone_result_button_next') }}
                    </div>
                    <div
                        v-if="isEndQuiz"
                        @click="handleClickNextToSummary"
                        :disabled="isEndEnable"
                        class="theme-button btn font-size-22 uppercase font-opensans font-size-22"
                        style="max-width: 240px"
                        id="end-btn"
                    >
                        {{ $t('playalone_result_button_end') }}
                    </div>
                </div>
            </div>
        </div>

        <CorrectAnswersPopup
            v-if="isShowCorrectAnswer"
            @close="isShowCorrectAnswer = false"
            :question="question"
            :show-title="false"
            :is-rect-bottom="true"
            :rect="popupCurrentRect"
            :left-gap="0"
            :is-animate="true"
        />

        <AuthPopup
            v-if="showAuthPopup"
            @login-success="loginSuccess"
            @register-success="loginSuccess"
            @close="showAuthPopup = false"
            :login-title="[$t('auth_header_login_see_correct')]"
            :webview-redirect-to="createWebviewRedirectTo()"
            :show-third-party-in-webview="false"
        />

        <PaymentPopup
            v-if="showPaymentPopup"
            @close="showPaymentPopup = false"
            @success="paymentSuccess"
            :quiz-id="quizId"
        />
    </section>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import ImageQuestion from '@/components/ImageQuestion.vue'
import ResultIconAnimation from '@/components/ResultIconAnimation.vue'
import Request from '@/lib/request.js'
import appConfig from '@/app.config.js'
import popup from '@/lib/popup.js'
import helper from '@/lib/helper.js'
import ResizeScale from '@/lib/resize-scale.js'
import QuizHeader from '@/components/QuizHeader.vue'
import Sound from '@/lib/sound.js'
import ClosePlayingButton from '@/components/ClosePlayingButton.vue'
import CorrectAnswersPopup from '@/components/CorrectAnswersPopup.vue'
import { isLoggedIn, validateToken } from '@/lib/user.js'
import AuthPopup from '@/components/AuthPopup'
import AuthPopupMixin from '@/mixins/auth-popup'
import setBackgroundQuestion from '@/mixins/set-bg-question.js'
import helperMixins from '@/mixins/helper.js'
import PaymentPopup from '@/components/PaymentPopup'
import BgMusicMuteBtn from '@/components/BgMusicMuteBtn.vue'

import plucky from '@/assets/sounds/plucky.mp3'
import wrong from '@/assets/sounds/wrong.mp3'
import swipe from '@/assets/sounds/swipe.mp3'

export default {
    components: {
        ImageQuestion,
        ResultIconAnimation,
        QuizHeader,
        ClosePlayingButton,
        CorrectAnswersPopup,
        AuthPopup,
        PaymentPopup,
        BgMusicMuteBtn
    },

    mixins: [AuthPopupMixin, setBackgroundQuestion, helperMixins],

    computed: {
        ...mapGetters({
            question: 'player/getCurrentQuiz',
            questionCount: 'player/getQuestionCount',
            audioSpotify: 'getSpotifyAudio'
        }),

        ...mapState({
            quizId: state => state.player.quiz._id.$oid,
            name: state => state.player.name,
            code: state => state.player.quiz.code,
            type: state => state.player.quiz.type,
            title: state => state.player.quiz.title,
            isEndQuiz: state => state.player.isEndQuiz,
            currentQuizIndex: state => state.player.currentQuizIndex,
            showSummary: state => state.player.quiz.show_result_on_user_device,
            quiz: state => state.player.quiz,
            showCorrectAnswerWhenWrong: state => state.player.quiz.show_correct_answer,
            orderAbility: state => state.orderAbility,
            tags: state => state.player.quiz.tags,
            isMuted: state => state.isMuted,
            embedToken: state => state.player.embedToken,
            isFrontPageQuiz: state => state.player.isFrontPageQuiz
        })
    },

    data: function () {
        return {
            xPos: null,
            isNextEnable: true,
            isEndEnable: true,
            userCount: 0,
            answeredUsers: 0,
            quizResults: [],
            playerResult: false,
            isAllowToPressNext: false,
            message: [],
            isAlreadyShow: false,
            nextPageCount: 0,
            sound: new Sound(),
            isShowCorrectAnswer: false,
            popupCurrentRect: null,
            showPaymentPopup: false,
            isClickEnd: false,
            showFooter: false
        }
    },

    created() {
        this.playerResult = this.$route.params.is_correct

        this.saveQuestionResult()

        this.sound.registerSound({ id: 'plucky', src: plucky })
        this.sound.registerSound({ id: 'wrong', src: wrong })
        this.sound.registerSound({ id: 'swipe', src: swipe })
    },

    mounted() {
        if (!helper.desktop() && document.getElementById('content-result')) {
            var elem = document.getElementById('content-result')

            elem.addEventListener('touchstart', evt => {
                this.xPos = evt.touches[0].clientX
            })
            document.addEventListener('touchmove', evt => {
                if (!this.xPos) {
                    return
                }

                var xMove = evt.touches[0].clientX
                var xDiff = this.xPos - xMove

                if (xDiff > 75 && !this.isShowCorrectAnswer) {
                    if (this.isEndQuiz) {
                        if (!this.isEndEnable) {
                            this.handleClickNextToSummary(this)
                        }
                    } else {
                        if (!this.isNextEnable) {
                            this.handleClickNext(this)
                        }
                    }
                }
            })
        }

        setTimeout(() => {
            if (!this.isMuted) {
                if (this.playerResult) {
                    this.sound.play('plucky')
                } else {
                    this.sound.play('wrong')
                }
            }
        }, 1500)

        window.onbeforeunload = function () {
            return 'Are you sure you want to leave this page? You will loose any unsaved actions'
        }

        if (this.audioSpotify) {
            this.audioSpotify.setAttribute('src', null)
        }

        if (this.question && this.question.title) {
            helper.quizFontSize('question-title', this.question.title.length)
        }

        if (isLoggedIn() && Object.keys(this.orderAbility).length === 0) {
            this.findOrderAbility()
        }
    },

    destroyed() {
        window.onbeforeunload = function () {}
    },

    methods: {
        eventNext(e) {
            if (e.target.classList.toString().indexOf('show-correct-btn') > -1 || this.isShowCorrectAnswer) {
                return
            }

            if (helper.desktop()) {
                if (this.isEndQuiz) {
                    this.handleClickNextToSummary(this)
                } else {
                    this.handleClickNext(this)
                }
            }
        },
        handleClickNext(e) {
            this.isNextEnable = true
            if (!this.isAllowToPressNext) {
                return typeof e.preventDefault == 'function' ? e.preventDefault() : null
            }

            if (this.nextPageCount > 0) {
                return
            }

            this.$store.dispatch('player/handleNextQuestionIndex')

            this.$router.replace({
                name: 'play-alone',
                params: {
                    code: this.$route.params.code
                }
            })

            if (!this.isMuted) {
                this.sound.play('swipe')
            }

            this.nextPageCount++
        },

        handleClickNextToSummary(e) {
            //(mixins) remove backgroundImage, video, fade
            this.removeBackdrop()
            this.removeVideoBackdrop()

            if (!this.isAllowToPressNext) {
                return typeof e.preventDefault == 'function' ? e.preventDefault() : null
            }

            if (this.isAllowToPressNext && document.querySelector('#end-btn')) {
                document.querySelector('#end-btn').style.pointerEvents = 'none'
            }

            if (this.isClickEnd) {
                return
            }

            this.isClickEnd = true

            return this.$router.replace({
                name: 'play-alone-summary',
                params: {
                    code: this.$route.params.code
                }
            })
        },

        saveQuestionResult() {
            var request = new Request()
            var formData = {
                choice: !['typing_question', 'sorting_list', 'puzzle'].includes(this.question.question_type)
                    ? this.$route.params.choice_selected.toString()
                    : this.playerResult
                    ? 0
                    : -1,
                question_id: this.question.uid
            }
            var url = appConfig.api.saveQuestionResult.replace('__id__', this.quizId)
            var self = this

            request
                .post(url, formData)
                .then(function () {
                    if (self.isFrontPageQuiz) {
                        self.fetchQuestionResult()
                    } else {
                        self.isEndEnable = false
                        self.isAllowToPressNext = true
                        self.isNextEnable = false

                        setTimeout(() => {
                            self.resizeScaleQuizFrame()
                        }, 100)
                    }
                })
                .catch(function (err) {
                    var errorMessage = err.response.data.message

                    self.$store.commit('updateMainModalMessage', errorMessage)
                    self.$store.commit('toggleMainModal', true)
                })
        },

        parseResultByLocale(messages) {
            const currentLocale = this.$i18n.locale
            const localePattern = /^\[(.*)\]/

            const enMessages = messages.filter(message => {
                return !message.match(localePattern)
            })

            if (currentLocale === 'en') {
                return enMessages
            }

            messages = messages.filter(message => {
                return message.match(new RegExp(`\\[${currentLocale}\\]`, 'g'))
            })

            if (!messages.length) {
                return enMessages
            }

            return messages.map(message => message.replace(localePattern, ''))
        },

        fetchQuestionResult() {
            var request = new Request()
            var url =
                appConfig.api.fetchQuestionResult.replace('__id__', this.quizId) +
                '?question_id[]=' +
                this.question.uid +
                `&lang=${this.$i18n.locale}`

            var self = this

            request
                .get(url)
                .then(function (res) {
                    if (self.playerResult) {
                        self.message = res.data.data[self.question.uid].correct_msg
                    } else {
                        self.message = res.data.data[self.question.uid].wrong_msg
                    }

                    self.message = self.parseResultByLocale(self.message)

                    self.isEndEnable = false
                    self.isAllowToPressNext = true
                    self.isNextEnable = false

                    setTimeout(() => {
                        self.resizeScaleQuizFrame()
                    }, 100)
                })
                .catch(function (err) {
                    var errorMessage = err.response.data.message

                    self.$store.commit('updateMainModalMessage', errorMessage)
                    self.$store.commit('toggleMainModal', true)
                })
        },

        resizeScaleQuizFrame() {
            const contentElem = document.getElementsByClassName('content-container')[0]
            const layoutElem = document.getElementsByClassName('layout')[0]
            const headerElem = document.getElementsByClassName('header')[0]
            const footerElem = document.getElementsByClassName('footer')[0]
            const footerHeight = footerElem.getBoundingClientRect().height
            const headerHeight = headerElem.getBoundingClientRect().height
            const uselessHeight = headerHeight + footerHeight

            layoutElem.style.setProperty('--useless-h', `${uselessHeight}px`)

            new ResizeScale({
                width: contentElem.getBoundingClientRect().width,
                height: contentElem.getBoundingClientRect().height,
                child: footerElem,
                parent: layoutElem,
                uselessHeight: uselessHeight,
                noTransForm: true
            })

            new ResizeScale({
                width: contentElem.getBoundingClientRect().width,
                height: contentElem.getBoundingClientRect().height,
                child: contentElem,
                parent: layoutElem,
                uselessHeight: uselessHeight,
                noTransForm: true
            })

            document.getElementById('app').classList.add('overflow-hidden')
            document.getElementById('app').classList.add('relative')

            this.isAlreadyShow = true
            this.showFooter = true
        },

        checkShowCorrectAnswerWhenWrong: function () {
            if (this.embedToken) {
                this.goDefaultPage()
                return
            }

            const rect = document.getElementById('question-header').getBoundingClientRect()
            const rectMobile = document.getElementById('question-header-mobile').getBoundingClientRect()

            this.popupCurrentRect = rect
            if (window.matchMedia('(max-width: 575px)').matches) {
                this.popupCurrentRect = rectMobile
            }

            if (!isLoggedIn()) {
                this.showAuthPopup = true
                return
            }

            validateToken()
                .then(() => {
                    this.isShowCorrectAnswer = true
                })
                .catch(() => {
                    this.showAuthPopup = true
                })
        },

        findOrderAbility: function () {
            if (isLoggedIn()) {
                this.$store.dispatch('findOrderAbility')
            }
        },

        paymentSuccess() {
            // this.findOrderAbility()
        },

        createWebviewRedirectTo() {
            var title = helper.slugify(this.quiz.title)
            return `/quiz/${this.quiz.code}/${title}`
        },

        setFormatText: helper.setFormatText,

        goDefaultPage(className) {
            if (this.embedToken) {
                if (className) {
                    document.querySelector(`.${className}`).setAttribute('href', '#')
                }
                window.open(this.quiz.url, '_blank')
            }
        }
    },

    beforeRouteLeave(to, from, next) {
        window.onbeforeunload = function () {}

        if (to.name === 'play-alone' || to.name === 'play-alone-summary') {
            next()
        } else {
            next(false)
            popup.confirm(
                this.$t('playalone_alert_leave'),
                function () {
                    window.location.href = '/'
                },
                function () {
                    window.onbeforeunload = function () {
                        return 'Are you sure you want to leave this page? You will loose any unsaved actions'
                    }
                }
            )
        }
    }
}
</script>

<style lang="scss" scoped>
.logo {
    @media (min-width: 767px) {
        width: 12rem;
    }
}

.icon {
    font-size: 5.5rem;
}

.layout {
    height: calc(100vh - 2rem);
    height: calc((var(--vh, 1vh) * 100) - 2rem);
    margin-top: -1rem;
    margin-right: -1rem;
    margin-left: -1rem;

    &.layout-scale {
        padding: 0;
    }

    .content {
        padding-left: 1rem;
        padding-right: 1rem;
    }
}

.empty-box-message {
    width: 20rem;
    height: 2rem;
}

section.layout .footer {
    padding: 0;
    position: absolute;
    right: 0;
    // bottom: 1rem;
    top: 90%;
    width: 100%;

    @media screen and (max-width: 767px) {
        top: 80%;
    }
}

@media screen and (max-width: 767px) {
    section.layout .content {
        overflow: hidden;
        height: calc(100% - var(--useless-h));
    }

    section.layout .footer {
        bottom: 0;
    }
}

@media screen and (max-width: 575px) {
    #question-header {
        display: none;
    }
}
</style>
